<template>
    <div class="stats-charts">
        <!-- График динамики по матчам -->
        <div v-if="chartData && chartData.length > 0" class="chart-section">
            <div class="chart-header">
                <h3 class="chart-title">Динамика по матчам</h3>
                <div class="chart-controls">
                    <UiSelect
                        v-model="selectedElement"
                        :options="elementOptions"
                        :block="false"
                        align="left"
                        value-type="string"
                    />
                    <UiSelect
                        v-model="selectedMetric"
                        :options="availableMetrics"
                        :block="false"
                        align="left"
                        value-type="string"
                    />
                </div>
            </div>
            <div class="chart-container">
                <Line v-if="ready" :data="chartDataset" :options="chartOptions" />
            </div>
        </div>

        <!-- График истории изменений -->
        <div v-if="historyData && historyData.length > 0" class="chart-section">
            <div class="chart-header">
                <h3 class="chart-title">История изменений</h3>
                <div class="chart-controls">
                    <UiSelect
                        v-model="historyPeriod"
                        :options="historyPeriodOptions"
                        :block="false"
                        align="left"
                        value-type="string"
                        @change="onPeriodChange"
                    />
                </div>
            </div>
            <div class="chart-container">
                <Line v-if="ready" :data="historyDataset" :options="historyChartOptions" />
            </div>
        </div>

        <!-- Пустое состояние -->
        <div v-if="(!chartData || chartData.length === 0) && (!historyData || historyData.length === 0)"
            class="empty-state">
            <div class="empty-icon">📈</div>
            <div class="empty-text">Нет данных для графиков</div>
            <div class="empty-hint">Примените фильтры для просмотра динамики</div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Line } from 'vue-chartjs';
import UiSelect from '@/components/ui/forms/UiSelect.vue';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const props = defineProps({
    chartData: {
        type: Array,
        default: () => [],
    },
    historyData: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['period-change']);

const ready = ref(false);
onMounted(() => {
    ready.value = true;
});

const selectedElement = ref('attack');
const selectedMetric = ref('kills');
const historyPeriod = ref('matches');
const elementOptions = [
    { value: 'attack', label: 'Атака' },
    { value: 'serve', label: 'Подача' },
    { value: 'block', label: 'Блок' },
    { value: 'reception', label: 'Прием' },
];
const historyPeriodOptions = [
    { value: 'matches', label: 'По матчам' },
    { value: 'seasons', label: 'По сезонам' },
];

// Метрики для каждого элемента
const elementMetrics = {
    attack: [
        { value: 'kills', label: 'Очки' },
        { value: 'efficiency', label: 'Эффективность' }
    ],
    serve: [
        { value: 'aces', label: 'Очки' },
        { value: 'efficiency', label: 'Эффективность' }
    ],
    block: [
        { value: 'kills', label: 'Очки' },
        { value: 'efficiency', label: 'Эффективность' }
    ],
    reception: [
        { value: 'perfect', label: 'Отличный, %' },
        { value: 'positive', label: 'Позитивный, %' },
        { value: 'efficiency', label: 'Эффективность' }
    ]
};

// Вычисляемые доступные метрики на основе выбранного элемента
const availableMetrics = computed(() => {
    return elementMetrics[selectedElement.value] || [];
});

// Вычисляемая метрика для графика
const computedMetric = computed(() => {
    const element = selectedElement.value;
    const metric = selectedMetric.value;

    // Маппинг метрик на поля данных
    const metricMap = {
        attack: {
            kills: 'attack_kills',
            efficiency: 'attack_efficiency'
        },
        serve: {
            aces: 'serve_aces',
            efficiency: 'serve_efficiency'
        },
        block: {
            kills: 'block_kills',
            efficiency: 'block_efficiency'
        },
        reception: {
            perfect: 'reception_perfect_pct',
            positive: 'reception_positive_pct',
            efficiency: 'reception_efficiency'
        }
    };

    return metricMap[element]?.[metric] || 'attack_efficiency';
});

// Следим за изменением элемента и сбрасываем метрику на первую доступную
watch(selectedElement, (newElement) => {
    const metrics = elementMetrics[newElement];
    if (metrics && metrics.length > 0) {
        selectedMetric.value = metrics[0].value;
    }
});

const chartOptions = computed(() => {
    // Метрики, которые должны отображаться только целыми числами
    const integerMetrics = ['attack_kills', 'serve_aces', 'block_kills', 'total_points'];
    // Метрики эффективности (могут быть отрицательными)
    const efficiencyMetrics = ['attack_efficiency', 'serve_efficiency', 'block_efficiency', 'reception_efficiency'];
    // Процентные метрики (не эффективность)
    const percentageMetrics = ['reception_perfect_pct', 'reception_positive_pct'];
    const currentMetric = computedMetric.value;
    const isIntegerMetric = integerMetrics.includes(currentMetric);
    const isEfficiencyMetric = efficiencyMetrics.includes(currentMetric);
    const isPercentageMetric = percentageMetrics.includes(currentMetric);

    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#1e293b',
                titleFont: { size: 12 },
                bodyFont: { size: 11 },
                padding: 10,
                cornerRadius: 6,
                callbacks: {
                    title: (items) => {
                        if (items.length > 0) {
                            const idx = items[0].dataIndex;
                            const match = props.chartData[idx];
                            if (match) {
                                // Форматируем дату
                                const dateStr = match.date_full || match.date || '';
                                let formattedDate = '';
                                if (dateStr) {
                                    try {
                                        const parts = dateStr.split('-');
                                        if (parts.length === 3) {
                                            const day = parseInt(parts[2], 10);
                                            const month = parseInt(parts[1], 10);
                                            formattedDate = `${day}.${month}`;
                                        } else {
                                            formattedDate = dateStr;
                                        }
                                    } catch (e) {
                                        formattedDate = dateStr;
                                    }
                                }
                                // Возвращаем дату и названия команд
                                const matchLabel = match.match_label || '';
                                return formattedDate ? `${formattedDate} ${matchLabel}`.trim() : matchLabel || '';
                            }
                        }
                        return '';
                    }
                }
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    font: { size: 11 },
                    color: '#64748b',
                }
            },
            y: {
                beginAtZero: isIntegerMetric || isPercentageMetric, // Для целочисленных и процентных метрик начинаем с 0
                min: (isIntegerMetric || isPercentageMetric) ? 0 : undefined, // Для эффективности может быть отрицательной
                max: isPercentageMetric ? 100 : undefined, // Для процентных метрик максимум 100%
                grid: {
                    color: '#e2e8f0',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11 },
                    color: '#64748b',
                    stepSize: isIntegerMetric ? 1 : undefined,
                    callback: function (value) {
                        // Для целочисленных метрик показываем только целые числа
                        if (isIntegerMetric) {
                            return Number.isInteger(value) ? value : '';
                        }
                        // Для метрик эффективности показываем все значения (включая отрицательные)
                        if (isEfficiencyMetric) {
                            return value;
                        }
                        // Для остальных процентных метрик
                        return value;
                    }
                }
            }
        },
        elements: {
            line: {
                tension: 0.3,
                borderWidth: 2,
            },
            point: {
                radius: 4,
                hoverRadius: 6,
            }
        }
    };
});

const historyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 12 },
            bodyFont: { size: 11 },
            padding: 10,
            cornerRadius: 6,
        }
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: {
                font: { size: 11 },
                color: '#64748b',
            }
        },
        y: {
            beginAtZero: false, // Эффективность может быть отрицательной
            grid: {
                color: '#e2e8f0',
                drawBorder: false,
            },
            ticks: {
                font: { size: 11 },
                color: '#64748b',
            }
        }
    },
    elements: {
        line: {
            tension: 0.3,
            borderWidth: 2,
        },
        point: {
            radius: 4,
            hoverRadius: 6,
        }
    }
};

const chartDataset = computed(() => {
    if (!props.chartData || props.chartData.length === 0) {
        return { labels: [], datasets: [] };
    }

    // Используем дату для подписи на оси X
    const labels = props.chartData.map(d => d.date || d.date_full || '');

    // Метрики, которые должны быть целыми числами
    const integerMetrics = ['attack_kills', 'serve_aces', 'block_kills', 'total_points'];
    const currentMetric = computedMetric.value;
    const isIntegerMetric = integerMetrics.includes(currentMetric);

    const data = props.chartData.map(d => {
        const value = d[currentMetric] || 0;
        // Для целочисленных метрик округляем до целого и убираем отрицательные
        if (isIntegerMetric) {
            return Math.max(0, Math.round(value));
        }
        // Для процентных метрик (эффективность) оставляем все значения, включая отрицательные
        return value;
    });

    const currentLabel = availableMetrics.value.find(m => m.value === selectedMetric.value)?.label || '';
    const elementLabel = {
        attack: 'Атака',
        serve: 'Подача',
        block: 'Блок',
        reception: 'Прием'
    }[selectedElement.value] || '';

    return {
        labels,
        datasets: [{
            label: `${elementLabel}: ${currentLabel}`,
            data,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
        }]
    };
});

const historyDataset = computed(() => {
    if (!props.historyData || props.historyData.length === 0) {
        return { labels: [], datasets: [] };
    }

    const labels = props.historyData.map(d => d.period || d.date || '');

    // Создаем несколько линий для разных метрик
    const datasets = [
        {
            label: 'Эффективность атаки',
            data: props.historyData.map(d => d.attack_efficiency || 0),
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
        },
        {
            label: 'Эффективность подачи',
            data: props.historyData.map(d => d.serve_efficiency || 0),
            borderColor: '#22c55e',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: false,
        },
        {
            label: 'Позитивный приём',
            data: props.historyData.map(d => d.reception_positive_pct || 0),
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: false,
        },
    ];

    return {
        labels,
        datasets,
    };
});

const onPeriodChange = () => {
    emit('period-change', historyPeriod.value);
};
</script>

<style lang="scss" scoped>
.stats-charts {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
}

.chart-section {
    background: #fff;
    border-radius: 10px;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid #f1f5f9;
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    gap: 1rem;
    flex-wrap: wrap;
}

.chart-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.chart-controls {
    display: flex;
    gap: 0.5rem;
}

.chart-container {
    height: 220px;
    position: relative;
}

.empty-state {
    background: #fff;
    border-radius: 10px;
    padding: 3rem 1.5rem;
    text-align: center;
    border: 1px solid #f1f5f9;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 0.75rem;
    opacity: 0.5;
}

.empty-text {
    font-size: 0.9rem;
    font-weight: 500;
    color: #475569;
    margin-bottom: 0.35rem;
}

.empty-hint {
    font-size: 0.8rem;
    color: #94a3b8;
}
</style>
